import { useState } from 'react'
import _ from 'lodash'
import { useErrorHandler } from 'react-error-boundary'
import { FormLoader, Toast, ProposalSection } from '@vzmi/shp_shared_compo'
import { PROPOSAL_REVIEW_STATUS } from '@vzmi/shp_shared_compo/constants/proposal'
import { delay } from '@vzmi/component_ec_nodejs_util/dist/lib/renderUtil'
import UiStateContainer from '@vzmi/ec-react-ui-state-container'
import { withCommonPageErrorBoundary } from '../../Common/HOC/withErrorBoundary'
import { withI13nWrapper } from '../../Common/I13nWrapper'
import withUpdateListingForm from '../../Proposal/Common/HOC/withUpdateListingForm'
import { FRONT_END_DEFINED_ERROR_CODE } from '../../../constants/globalTypes'
import { PROPOSAL_TYPE_NAME } from '../../../constants/proposalTypes'
import ProposalConsent from '../../Proposal/Common/ProposalConsent/ProposalConsent'
import ProposalCompletion from '../../Proposal/Common/ProposalCompletion/ProposalCompletion'
import { getFormValues } from '../../Proposal/Common/ProposalForm/Shared/util'
import ContentLayout from '../Common/ContentLayout'
import ActionHeader from '../Common/ActionHeader'
import { ActionFooterWithUIState } from '../Common/ActionFooter'
import Background from '../Common/Background'
import BackToListingQueryLink from '../Common/BackToListingQueryLink'
import UpdateFailedListingsCard from '../Common/Card/UpdateFailedListings'
import UpdatedListingsTableCard from '../Common/Card/UpdatedListingsTable'
import UpdatedListingsBatchCard from '../Common/UpdatedListingsBatchCard'
import NotFoundListingsSection from '../Common/Section/NotFoundListings'
import PreSubmitModalContent from '../Common/PreSubmitModalContent'
import UpdateFailedListingsSection from '../Common/Section/UpdateFailedListings'
import batchProposals from '../request/batchProposals'
import useBeforeunload from '../hook/useBeforeunload'
import usePageFlow from '../hook/usePageFlow'
import useI13N from '../hook/useI13N'
import useBackgroundFormDataOnInit from '../hook/useBackgroundFormDataOnInit'
import useListingsOnComposeInit from '../hook/useListingsOnComposeInit'
import useProposalsOnOverviewInit from '../hook/useProposalsOnOverviewInit'
import { useQueryString } from '@vzmi/shp_shared_compo/hook/useQueryString'
import useUIState from '../hook/useUIState'
import useListingState from '../hook/useListingState'
import { FlowContext } from '../helper/flow'
import {
  getBatchResult,
  getListingsWithProposal,
  getCombinedBatchResult,
  getDuplicatedListingsGroupByListingId
} from '../helper/batch'
import {
  getFormStateFromStorage,
  clearStateFromStorage
} from '../helper/storage'
import {
  getListingWithImagesModels,
  getParamsList,
  getBatchProposalParams,
  getUpdateListingBatchFields,
  getParamsListWithBatch,
  getBatchDetailParams,
  getBatchId
} from './helper'
import ListingImageRows from './ListingImageRows'
import ScheduledBlock, {
  SECTION as scheduledSection,
  FIELD as scheduledField,
  getScheduledFromBatch,
  getGAReservation
} from '../Common/ScheduledBlock'
import createBatch from '../request/createBatch'
import updateBatch from '../request/updateBatch'
import ScheduledStatus from '../Common/ScheduledStatus'
import { HyperLink } from '../../Common/Form'
import styles from './index.css'
import ComposeBlock from './ComposeBlock'

const UpdateImage = props => {
  const {
    match: { params: { phase } },
    dispatch,
    change,
    initialize,
    actions,
    flags,
    user,
    proposalType,
    reduxFormState,
    i13nMethods,
    s3Service: {
      fetchBase64Yimg
    } = {},
    getWssId,
    getCredentials,
    transformS3Object,
    i13n
  } = props
  const s3ServiceActions = {
    getWssId,
    getCredentials,
    transformS3Object,
    fetchBase64Yimg
  }

  const formValues = getFormValues(reduxFormState)
  const {
    scheduled = {}
  } = formValues
  const {
    [scheduledField.IS_SCHEDULED]: isScheduled,
    [scheduledField.IS_REVERT]: isRevert
  } = scheduled
  const isBatch = (isScheduled || isRevert)

  const [proposals, setProposals] = useState([])
  const [batch, setBatch] = useState({})
  const [rowsKey, setRowsKey] = useState(Date.now())
  const { state: pageState, actions: pageActions } = useUIState(props)
  const { state: listingState, actions: listingActions } = useListingState()
  const {
    listings,
    deletedListings,
    queryStringListingIds,
    batchResult
  } = listingState
  const { successProposalMap, failedProposalMap } = batchResult
  const pageFlow = usePageFlow(phase, pageState.pageFlow)
  const {
    isEditPage,
    isConfirmPage,
    isSuccessPage,
    isOverviewPage,
    readOnly
  } = pageFlow

  const currentQueryStringListingIds = useQueryString(props, 'listingIds', true)
  const reservation = getGAReservation(scheduled)

  // 在修改、確認、完成頁加上 GA 紀錄
  const i13nData = {
    proposalType,
    queryStringListingIds: currentQueryStringListingIds,
    reservation
  }
  useI13N(i13nMethods, i13nData, pageFlow)

  // 修改頁綁上離開頁面確認事件
  useBeforeunload(isOverviewPage)

  // 頁面初始化寫入賣場背景資料
  useBackgroundFormDataOnInit(props, initialize)

  const [
    duplicatedMap,
    setDuplicatedMap
  ] = useState({})
  const listingsParams = { id: currentQueryStringListingIds }
  const handleError = useErrorHandler()
  // 修改頁初始化取得批次賣場資料
  useListingsOnComposeInit(props, listingsParams, isEditPage,
    () => pageActions.setIsLoading(true),
    (error, fetchedListings) => {
      if (error) {
        pageActions.setIsLoading(false)
        Toast.error(error.message)
        return
      }

      // 查無賣場
      if (_.isEmpty(fetchedListings)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_LISTING_NOT_FOUND })
        return
      }

      const savedFormListings = _.get(getFormStateFromStorage(), 'listings')
      // 如果 okta 逾時重新取得 token 回來頁面會直接使用 localstorage 儲存的賣場修改狀態
      const currentListings = savedFormListings || fetchedListings
      const listingsWithImagesModels = currentListings.map(getListingWithImagesModels)
      // 避免原始狀態拿到 localstorage 存到的賣場是已修改過的
      // 如果 localstorage 有資料，改成以 get listing 資料為原始資料
      const originListingsWithImagesModels = savedFormListings
        ? fetchedListings.map(getListingWithImagesModels)
        : listingsWithImagesModels
      const currentDuplicatedMap = getDuplicatedListingsGroupByListingId(currentListings)
      pageActions.setIsLoading(false)
      listingActions.setListings(listingsWithImagesModels)
      listingActions.setQueryStringListingIds(currentQueryStringListingIds)
      setDuplicatedMap(currentDuplicatedMap)
      changeFormListings(listingsWithImagesModels)
      change('originListings', originListingsWithImagesModels)
      clearStateFromStorage()
    }
  )

  const currentQueryStringProposalIds = useQueryString(props, 'ids', true)
  const currentQueryStringBatchId = useQueryString(props, 'batchId', true)
  const currentQueryStringBatchType = useQueryString(props, 'type', false)
  const proposalsParams = {
    proposal: { id: currentQueryStringProposalIds },
    batch: {
      id: currentQueryStringBatchId,
      type: currentQueryStringBatchType
    }
  }
  // 檢視頁初始化取得批次提案資料
  useProposalsOnOverviewInit(props, proposalsParams, isOverviewPage,
    () => pageActions.setIsLoading(true),
    (error, currentProposals, currentListings, currentBatch) => {
      if (error) {
        pageActions.setIsLoading(false)
        Toast.error(error.message)
        return
      }

      // 查無提案
      if (_.isEmpty(currentProposals)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_PROPOSAL_NOT_FOUND })
        return
      }

      // 查無賣場
      if (_.isEmpty(currentListings)) {
        handleError({ code: FRONT_END_DEFINED_ERROR_CODE.FE_LISTING_NOT_FOUND })
        return
      }

      const scheduled = getScheduledFromBatch(currentBatch)
      const currentBatchResult = getBatchResult(currentProposals.map(proposal => ([undefined, proposal])))
      const covertedListings = _.flow(
        () => getListingsWithProposal(currentListings, currentBatchResult),
        listingsWithProposalId => _.map(listingsWithProposalId, getListingWithImagesModels)
      )()
      pageActions.setIsLoading(false)
      listingActions.setListings(covertedListings)
      listingActions.setBatchResult(currentBatchResult)
      changeFormListings(covertedListings)
      change(scheduledSection, scheduled)
      setProposals(currentProposals)
      setBatch(currentBatch)
    }
  )

  // 修改 redux form 裡的賣場
  const changeFormListings = listings => change('listings', listings)

  const changeAndForceReloadListings = async (listings) => {
    listingActions.setListings(listings)
    changeFormListings(listings)
    await delay(100)
    setRowsKey(Date.now())
  }

  // 完成提案
  const onComplete = async () => {
    const formValues = getFormValues(reduxFormState)
    const formListings = _.get(formValues, 'listings', [])
    const paramsList = getParamsList(
      getFormValues(reduxFormState),
      PROPOSAL_REVIEW_STATUS.DRAFT,
      duplicatedMap
    )
    const proposals = await batchProposals(actions, paramsList)
    const combinedBatchResult = getCombinedBatchResult(
      batchResult,
      getBatchResult(proposals)
    )
    const { successProposalMap } = combinedBatchResult
    const covertedListings = _.flow(
      // 完成提案後用含有提案編號的資料取代原本的賣場資料
      // 送出提案、返回修改再次完成提案時才有 proposalId 可以使用
      () => getListingsWithProposal(formListings, combinedBatchResult),
      // 從 api 取得的賣場資料要再轉換成前端格式給 component 使用
      listingsWithProposalId => _.map(listingsWithProposalId, getListingWithImagesModels)
    )()
    const updatedListings = _.filter(covertedListings, listing => {
      return (listing.id in successProposalMap)
    })
    listingActions.setListings(covertedListings)
    listingActions.setBatchResult(combinedBatchResult)
    changeFormListings(covertedListings)
    return [null, updatedListings]
  }

  // 送出提案
  const onPendingReview = async () => {
    const { failedProposalMap } = batchResult
    const formValues = getFormValues(reduxFormState)
    let batches
    if (isBatch) {
      const batchProposalParams = getBatchProposalParams(scheduled)
      // 有預約、回檔時送出提案會先產生批次編號
      const [error, payload] = await createBatch(actions, batchProposalParams)
      if (error) {
        console.log(error)
        return [_.get(error, 'message', '預約失敗，請稍候再試')]
      }
      batches = getUpdateListingBatchFields(payload)
    }
    const getParamsListByIsRevert = isRevert => {
      const originListings = _.get(formValues, 'originListings', [])
      const paramsList = _.filter(
        getParamsList(
          // 回檔賣場為恢復修改前狀態，所以會用賣場原始資料
          isRevert ? { ...formValues, listings: originListings } : formValues,
          // 預設非預約回檔在送出提案時固定都是 pendingReview
          PROPOSAL_REVIEW_STATUS.PENDING_REVIEW,
          duplicatedMap
        ),
        // 送出提案過濾掉完成提案時失敗的提案
        params => !(_.get(params, 'listing.id') in failedProposalMap)
      )
      return paramsList
    }
    let proposals
    const isBatchEmpty = _.isEmpty(batches)
    if (isBatchEmpty) {
      const paramsList = getParamsListByIsRevert(false)
      proposals = await batchProposals(actions, paramsList)
    } else {
      const paramsListWithBatch = getParamsListWithBatch(getParamsListByIsRevert, batches)
      proposals = await batchProposals(actions, paramsListWithBatch)
      const batchDetailParams = getBatchDetailParams(batches, proposals)
      const [error] = await updateBatch(actions, batchDetailParams)
      if (error) {
        console.log(error)
        return [_.get(error, 'message', '寫入預約失敗，請稍候再試')]
      }
    }
    const combinedBatchResult = getCombinedBatchResult(
      batchResult,
      getBatchResult(proposals)
    )
    const { successProposalMap } = combinedBatchResult
    const listingsWithProposal = getListingsWithProposal(listings, combinedBatchResult, batches)
    const updatedListings = _.filter(listingsWithProposal, listing => {
      return (listing.id in successProposalMap)
    })
    listingActions.setListings(listingsWithProposal)
    listingActions.setBatchResult(combinedBatchResult)
    changeFormListings(listingsWithProposal)
    return [null, updatedListings]
  }

  // 下次再改按鈕觸發刪除該賣場於 state 跟 redux form 的資料
  // 並重新計算可能含有共用商品的賣場
  const onRemoveListing = async (id, listings) => {
    const newListings = []
    const deletedListings = []
    for (const listing of listings) {
      if (listing.id === id) {
        deletedListings.push(listing)
      } else {
        newListings.push(listing)
      }
    }
    const currentDuplicatedMap = getDuplicatedListingsGroupByListingId(newListings)
    // state 紀錄被刪除的賣場、更新既有賣場
    listingActions.addDeletedListings(deletedListings)
    // redux form 更新賣場欄位
    changeFormListings(newListings)
    // state 重新計算含有共用商品的賣場
    setDuplicatedMap(currentDuplicatedMap)
  }

  // header footer context
  // 避免在主要表單中使用此 context 避免重複 render component
  const flowContextValue = {
    phase,
    pageFlow: pageState.pageFlow,
    isLoading: pageState.isLoading,
    isPending: pageState.isPending,
    setPageFlow: pageActions.setPageFlow,
    setIsLoading: pageActions.setIsLoading,
    setIsPending: pageActions.setIsPending
  }
  const proposalTypeName = `批次${PROPOSAL_TYPE_NAME[proposalType]}`
  const completeName = `${proposalTypeName}-WEB已送出`

  if (_.isEmpty(listings)) {
    return (
      <FormLoader
        isLoading
        fixed
      />
    )
  }

  const batchId = getBatchId(successProposalMap)
  return (
    <ContentLayout isSuccessPage={isSuccessPage}>
      <FormLoader
        isLoading={pageState.isLoading}
        fixed
      >
        {
          // 完成頁
          isSuccessPage && (
            <>
              <ProposalCompletion
                title={
                  isBatch
                    ? (
                      <span>
                        {`${completeName}(批次編號：`}
                        <HyperLink
                          target='_blank'
                          href={`/proposal/batch/query?id=${batchId}`}
                        >
                          {batchId}
                        </HyperLink>
                        {')'}
                      </span>
                    )
                    : completeName
                }
                proposalType={proposalType}
                withHint={false}
              />
              <div className={styles.linkContent}>
                <HyperLink
                  target='_blank'
                  href='/proposal/batch/query'
                >
                  查詢批次修改圖片
                </HyperLink>
              </div>
              {!isBatch && (
                <>
                  <UpdateFailedListingsCard
                    proposalTypeName={proposalTypeName}
                    listings={listings}
                    failedMap={failedProposalMap}
                  />
                  <UpdatedListingsTableCard
                    proposalType={proposalType}
                    listings={listings}
                    successMap={successProposalMap}
                  />
                </>
              )}
              {isBatch && (
                <UpdatedListingsBatchCard
                  proposalType={proposalType}
                  listings={listings}
                  successBatchMap={successProposalMap}
                  failedBatchMap={failedProposalMap}
                />
              )}
            </>
          )
        }
        {
          // 修改、確認、檢視頁
          !isSuccessPage && (
            <UiStateContainer>
              <FlowContext.Provider value={flowContextValue}>
                <ActionHeader
                  title={proposalTypeName}
                  subtitle={
                    (isOverviewPage && !_.isUndefined(batch.id))
                      ? `批次編號：${batch.id}`
                      : ''
                  }
                  listingIds={queryStringListingIds}
                />
              </FlowContext.Provider>
              {(readOnly && isOverviewPage) && (
                <ScheduledStatus
                  proposals={proposals}
                  batch={batch}
                  batchResult={batchResult}
                />
              )}
              <Background
                flags={flags}
                user={user}
                isEditPage={isEditPage}
                readOnly={readOnly}
              />
              {isConfirmPage && <ProposalConsent />}
              <NotFoundListingsSection
                listingIdsFromUrl={queryStringListingIds}
                listings={listings}
                deletedListings={deletedListings}
                isVisible={isEditPage}
              />
              <UpdateFailedListingsSection
                proposalTypeName={proposalTypeName}
                listings={listings}
                failedMap={failedProposalMap}
              />
              <ScheduledBlock
                change={change}
                readOnly={readOnly}
              />
              {isEditPage && (
                <ComposeBlock
                  s3ServiceActions={s3ServiceActions}
                  pageActions={pageActions}
                  changeFormListings={changeAndForceReloadListings}
                  i13n={i13n}
                />
              )}
              <ProposalSection
                title='賣場/商品圖片'
                data-role='batch-update-image'
              >
                <ListingImageRows
                  key={rowsKey}
                  readOnly={readOnly}
                  onRemove={onRemoveListing}
                  batchResult={batchResult}
                  duplicatedMap={duplicatedMap}
                  isOverviewPage={isOverviewPage}
                  proposalType={proposalType}
                />
              </ProposalSection>
              <FlowContext.Provider value={flowContextValue}>
                <ActionFooterWithUIState
                  dispatch={dispatch}
                  batchResult={batchResult}
                  onComplete={onComplete}
                  onPendingReview={onPendingReview}
                  preSubmitContent={(
                    <PreSubmitModalContent
                      duplicatedMap={duplicatedMap}
                    />
                  )}
                  withPreSubmit={!_.isEmpty(duplicatedMap)}
                />
              </FlowContext.Provider>
            </UiStateContainer>
          )
        }
      </FormLoader>
    </ContentLayout>
  )
}

const hoc = _.flow([
  withI13nWrapper,
  // common page: yahoo title & supplier part
  // error boundary
  withCommonPageErrorBoundary(<BackToListingQueryLink />),
  // proposal redux form init
  withUpdateListingForm
])

export default hoc(UpdateImage)
